import { UTILITY_PIPELINE_FIELD_KEYS } from './sed/utilityPipelineDocumentProjection';
import type { DocumentExtractionOutcomeV1 } from '../types/siteRag';
import { SED_SECTION_IDS, type EvidenceSection, type ExternalLiteratureSection, type PublicSentimentSection, type SedSectionId } from '../types/siteEvidence';

// Literature and sentiment have their own canonical payloads, without field-mapping data.
// Accept those real SED slots without pretending they are EvidenceSections.
type MappingEvidence = Partial<Record<Exclude<SedSectionId, 'externalLiterature' | 'publicSentiment'>,
  Pick<EvidenceSection<unknown>, 'data' | 'docFieldSources' | 'linkedDocumentIds' | 'provenanceDetail'>>>
  & { externalLiterature?: ExternalLiteratureSection; publicSentiment?: PublicSentimentSection };

/** Retained, exact per-field source bindings; not a source grade or requirement-completion rule. */
export function documentMappingCounts(sed: MappingEvidence, docId: string): {
  retainedFieldCount: number; linkedSectionCount: number; populatedSlotCount: number;
} {
  const fields = new Set<string>();
  const linked = new Set<string>();
  const fieldSections = new Set<string>();
  for (const sectionId of SED_SECTION_IDS) {
    if (sectionId === 'externalLiterature' || sectionId === 'publicSentiment') continue;
    const slot = sed[sectionId];
    if (!slot) continue;
    const detail = slot.provenanceDetail;
    const rejected = (field: string) => detail?.fieldReviews?.[field]?.decision === 'rejected';
    const retain = (field: string, value: unknown, sourceValue: unknown, identity = `${sectionId}.${field}`) => {
      if (value === undefined || value === null || rejected(field)
        || (typeof value === 'number' && !Number.isFinite(value))
        || (typeof value === 'string' && !value.trim())
        || (Array.isArray(value) && value.length === 0)
        || JSON.stringify(value) !== JSON.stringify(sourceValue)) return;
      fields.add(identity); fieldSections.add(sectionId);
    };
    for (const [field, source] of Object.entries(detail?.autoFillFieldSources ?? {})) {
      if (source.docId !== docId) continue;
      // A typed pipeline projection preserves the original grid receipt. Both
      // are the same source leaf only when document and exact value still agree.
      const infrastructure = sed.onSiteInfrastructure;
      const typed = infrastructure?.data && typeof infrastructure.data === 'object'
        ? infrastructure.data as Record<string, unknown> : {};
      const projected = infrastructure?.docFieldSources?.[field];
      const projectedRejected = infrastructure?.provenanceDetail?.fieldReviews?.[field]?.decision === 'rejected';
      const sameProjection = sectionId === 'grid' && UTILITY_PIPELINE_FIELD_KEYS.some(key => key === field)
        && projected?.docId === source.docId && !projectedRejected
        && JSON.stringify(typed[field]) === JSON.stringify(source.value)
        && JSON.stringify(projected.value) === JSON.stringify(source.value);
      retain(field, detail?.autoFillFields?.[field], source.value,
        sameProjection ? `onSiteInfrastructure.${field}` : `${sectionId}.${field}`);
    }
    const data = slot.data && typeof slot.data === 'object' ? slot.data as Record<string, unknown> : {};
    for (const [field, source] of Object.entries(slot.docFieldSources ?? {})) {
      if (source.docId === docId) retain(field, data[field], source.value);
    }
    if (slot.linkedDocumentIds?.includes(docId)) linked.add(sectionId);
  }
  // A linked section is a legacy mapping unit only when it has no separately
  // counted fields from this doc. The two channels cannot double-count a field.
  const legacySections = [...linked].filter(section => !fieldSections.has(section));
  return { retainedFieldCount: fields.size, linkedSectionCount: linked.size,
    populatedSlotCount: fields.size + legacySections.length };
}

export function documentMappingSummary(outcome: DocumentExtractionOutcomeV1): {
  deferred: number; failed: number; empty: number; unsupported: number; rejected: number;
} {
  const count = (status: DocumentExtractionOutcomeV1['targets'][number]['status']) => outcome.targets.filter(target => target.status === status).length;
  return { deferred: count('deferred'), failed: count('failed'), empty: count('empty'), unsupported: count('unsupported'), rejected: count('rejected') };
}
